import React, {
  useState,
  useEffect,
  useMemo,
} from 'react';

import {
  getDailyTip,
  getTipTypeLabel,
} from '../../utils/dailyTip';

import {
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { useKidAuth } from '../../contexts/KidAuthContext';

import { dbKidData as db } from '../../services/supabase';

import { formatCurrency } from '../../utils/currency';

import {
  buildUnifiedActivity,
  getGoalBalances,
} from '../../utils/activity';

import { useBalance } from '../../hooks/useBalance';
import { getKidTheme } from '../../utils/kidThemes';

import KidLayout from '../../components/Layout/KidLayout';
import KidOnboarding from '../../components/Onboarding/KidOnboarding';
import KidDashboardTour from '../../components/Onboarding/KidDashboardTour';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import MyPiggyIcon from '../../components/UI/MyPiggyIcon';
import InfoTooltip from '../../components/UI/InfoTooltip';
import '../../styles/brand-typography.css';
import './KidDashboard.css';
import KidSubscriptionCard from '../../components/Family/KidSubscriptionCard';


// ==================================================
// Balance Card
// ==================================================

const BalanceCard = ({ title, icon, amount, info, loading, error, className = '' }) => (
  <Card className={`kid-balance-card p-3 text-white ${className}`}>
    <div className="flex items-start gap-1.5">
      <MyPiggyIcon name={icon} size="xs" />
      <p className="min-w-0 flex-1 text-xs font-extrabold leading-snug sm:text-sm">{title}</p>
      <InfoTooltip compact label={`What is ${title}?`}>{info}</InfoTooltip>
    </div>
    <p className="mt-2 break-words text-center text-xl font-extrabold sm:text-2xl">{loading ? 'Loading…' : error ? 'Unavailable' : formatCurrency(amount)}</p>
  </Card>
);

const KidDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const dailyTip = getDailyTip('kid');

  const {
    currentKid,
    kidFamily,
    refreshKid,
    logoutKid,
    canWrite: familyAccessCanWrite,
  } = useKidAuth();

  const isWriteLocked =
    familyAccessCanWrite === false;


  // ==================================================
  // Balance
  // ==================================================

  const {
    balance,
    savingsBalance,
    loading: balanceLoading,
    error: balanceError,
    refresh: refreshBalance,
  } = useBalance(
    currentKid?.id,
    currentKid?.family_id,
    'kid'
  );


  // ==================================================
  // Dashboard State
  // ==================================================

  const [
    savingsGoals,
    setSavingsGoals,
  ] = useState([]);

  const [
    savingsLedger,
    setSavingsLedger,
  ] = useState([]);

  const [
    transactions,
    setTransactions,
  ] = useState([]);

  const [
    recentActivity,
    setRecentActivity,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    dashboardError,
    setDashboardError,
  ] = useState('');


  const [
    showReplayTour,
    setShowReplayTour,
  ] = useState(
    Boolean(location.state?.replayKidTour)
  );

  useEffect(() => {
    if (location.state?.replayKidTour) {
      setShowReplayTour(true);
      navigate(location.pathname, {
        replace: true,
        state: {},
      });
    }
  }, [
    location.pathname,
    location.state,
    navigate,
  ]);


  // ==================================================
  // Dashboard Theme
  // ==================================================

  const themeConfig = getKidTheme(
    currentKid?.dashboard_theme || 'sunset'
  );


  // ==================================================
  // Browser Title
  // ==================================================

  useEffect(() => {
    const name =
      currentKid?.nickname ||
      currentKid?.full_name ||
      'My';

    document.title =
      `${name}’s Piggy Bank 🐷`;

    return () => {
      document.title =
        'MyPiggy Kids';
    };
  }, [currentKid]);


  // ==================================================
  // Load Dashboard
  // ==================================================

  useEffect(() => {
    if (
      currentKid?.id &&
      currentKid?.family_id
    ) {
      loadDashboardData();
    }
  }, [
    currentKid?.id,
    currentKid?.family_id,
  ]);


  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setDashboardError('');

      const [
        goals,
        transactionData,
        ledgerData,
      ] = await Promise.all([
        db.getSavingsGoals(
          currentKid.id
        ),

        db.getTransactions(
          currentKid.family_id,
          currentKid.id
        ),

        db.getSavingsLedger(
          currentKid.id
        ),
      ]);


      // Active goals

      const activeGoals =
        (goals || []).filter(
          (goal) =>
            goal.status === 'active'
        );

      setSavingsGoals(activeGoals);
      setSavingsLedger(ledgerData || []);

      setTransactions(
        transactionData || []
      );


      // Recent Activity

      setRecentActivity(
        buildUnifiedActivity({
          transactions:
            transactionData || [],

          savingsLedger:
            ledgerData || [],

          goals:
            goals || [],
        }).slice(0, 3)
      );

    } catch (error) {
      console.error(
        'Error loading Kid Dashboard data:',
        error
      );

      setDashboardError(
        error?.message ||
          'Unable to load your dashboard. Please try again.'
      );

    } finally {
      setLoading(false);
    }
  };


  // ==================================================
  // Pending Chore Transactions
  // ==================================================

  const pendingChoreTransactions =
    useMemo(() => {
      return (
        transactions || []
      ).filter(
        (transaction) =>
          transaction.type === 'income' &&
          transaction.category === 'Chores' &&
          transaction.approval_status === 'pending'
      );
    }, [transactions]);


  // ==================================================
  // Pending Reward Total
  // ==================================================

  const pendingChoreAmount =
    useMemo(() => {
      return pendingChoreTransactions.reduce(
        (
          total,
          transaction
        ) =>
          total +
          (
            Number(
              transaction.amount_cents
            ) || 0
          ),
        0
      );
    }, [
      pendingChoreTransactions,
    ]);


  // ==================================================
  // Goal Balances
  // ==================================================

  const goalBalances = useMemo(
    () =>
      getGoalBalances(
        savingsGoals,
        savingsLedger
      ),
    [
      savingsGoals,
      savingsLedger,
    ]
  );


  const getSavingsProgress = (
    goal
  ) => {
    if (
      !goal?.target_amount_cents
    ) {
      return 0;
    }

    const saved =
      Math.max(
        0,
        goalBalances.get(
          goal.id
        ) || 0
      );

    return Math.min(
      (
        saved /
        Number(
          goal.target_amount_cents
        )
      ) * 100,
      100
    );
  };


  // ==================================================
  // Missing Kid
  // ==================================================

  if (!currentKid) {
    navigate('/kid/dashboard');
    return null;
  }


  // ==================================================
  // Loading Screen
  // ==================================================

  if (loading) {
    return (
      <KidLayout
        className="mypiggy-brand kid-dashboard"
        title={`Hi, ${currentKid.nickname}!`}
        avatar={currentKid.avatar}
      >
        <div className="text-center py-8 text-white">
          Loading...
        </div>
      </KidLayout>
    );
  }


  // ==================================================
  // Dashboard
  // ==================================================

  const actions = [
    { label: 'Add Money', icon: 'add-money', route: '/kid/add-income', tour: 'kid-add-money', color: 'green', write: true },
    { label: 'Spent Money', icon: 'spent-money', route: '/kid/add-expense', tour: 'kid-spent-money', color: 'orange', write: true },
    { label: 'My Savings', icon: 'my-savings', route: '/kid/savings', tour: 'kid-savings', color: 'pink' },
    { label: 'Do Chores', icon: 'do-chores', route: '/kid/chores', tour: 'kid-chores', color: 'teal' },
    { label: 'View My Reports', icon: 'view-my-reports', route: '/kid/reports', tour: 'kid-reports', color: 'purple' },
  ];

  return (
    <KidLayout className="mypiggy-brand kid-dashboard" title={`Hi, ${currentKid.nickname}!`} avatar={currentKid.avatar}>
      <div className="space-y-4">
        <KidSubscriptionCard attentionOnly />
        {dashboardError && <Card className="border-red-200 bg-red-50 p-3"><p role="alert" className="text-sm text-red-700">{dashboardError}</p></Card>}
        {balanceError && <Card className="border-red-200 bg-red-50 p-3"><p role="alert" className="text-sm text-red-700">Unable to load your current balance.</p></Card>}

        <section aria-label="Your money" className="grid grid-cols-2 gap-3">
          <BalanceCard title="Available Money" icon="balance" amount={balance} loading={balanceLoading} error={balanceError} info="Money currently free to use or save." className={themeConfig.availableCardClass} />
          <BalanceCard title="Saved for Goals" icon="my-savings" amount={savingsBalance} loading={balanceLoading} error={balanceError} info="Money you have set aside for your savings goals." className={themeConfig.savingsCardClass} />
        </section>

        <section aria-label="What would you like to do?" className="grid grid-cols-2 gap-2.5">
          {actions.map((action) => (
            <Button key={action.route} data-tour={action.tour} variant="secondary" disabled={action.write && isWriteLocked}
              onClick={() => navigate(action.route)} className={`kid-task kid-task--${action.color} ${action.tour === 'kid-reports' ? 'col-span-2' : ''}`}>
              <MyPiggyIcon name={action.icon} size="sm" />
              <span className="min-w-0 text-base sm:text-lg font-extrabold leading-snug">{action.label}</span>
            </Button>
          ))}
        </section>

        {pendingChoreTransactions.length > 0 && (
          <Card className="border-amber-200 bg-amber-50 p-3">
            <div className="flex items-center gap-2"><MyPiggyIcon name="chores-submitted-for-approval" size="sm" /><div className="min-w-0 flex-1"><h2 className="text-sm font-extrabold text-amber-900">Waiting for Parent</h2><p className="text-xs text-amber-900">{pendingChoreTransactions.length} chore{pendingChoreTransactions.length === 1 ? '' : 's'} · {formatCurrency(pendingChoreAmount)}</p></div><Button size="sm" variant="secondary" onClick={() => navigate('/kid/chores')}>View Chores</Button></div>
            <p className="mt-1 text-xs text-amber-900">Rewards are added after your parent approves them.</p>
          </Card>
        )}

        {!balanceError && !dashboardError && (
          <Card className="p-3 sm:p-4">
            <div className="mb-3 flex items-center gap-2"><MyPiggyIcon name="savings-goal" size="xs" /><h2 className="flex-1 text-base font-extrabold text-slate-900">My Savings Goals</h2><span className="text-xs text-slate-600">{savingsGoals.length} active</span></div>
            {savingsGoals.length ? <div className="space-y-3">{[...savingsGoals].sort((a, b) =>
              getSavingsProgress(b) - getSavingsProgress(a)
            ).map((goal) => {
              const saved = Math.max(0, goalBalances.get(goal.id) || 0);
              const progress = getSavingsProgress(goal);
              return <div key={goal.id} className="rounded-lg border border-slate-200 bg-slate-50/60 p-2.5">
                <div className="mb-1 flex items-start justify-between gap-3"><h3 className="min-w-0 break-words text-sm font-bold text-slate-900">{goal.name}</h3><span className="shrink-0 text-xs font-extrabold text-purple-700">{Math.round(progress)}%</span></div>
                <div role="progressbar" aria-label={`${goal.name} savings progress`} aria-valuenow={Math.round(progress)} aria-valuemin={0} aria-valuemax={100} className="h-2 overflow-hidden rounded-full bg-slate-200"><div className={`h-full rounded-full ${themeConfig.accentButtonClass}`} style={{ width: `${progress}%` }} /></div>
                <p className="mt-1 text-xs text-slate-600">{formatCurrency(saved)} of {formatCurrency(goal.target_amount_cents)}</p>
              </div>;
            })}</div> : <p className="text-sm text-slate-600">Saving for something special? Start a goal in My Savings.</p>}
          </Card>
        )}

        {dailyTip && <details className="kid-money-tip rounded-xl border border-amber-200 bg-amber-50 p-3 shadow-sm">
          <summary className="flex min-h-11 cursor-pointer list-none items-center gap-2 text-slate-900">
            <MyPiggyIcon name="learning-tip" size="sm" /><span className="min-w-0 flex-1"><span className="block text-sm font-extrabold">Today’s Money Tip</span><span className="block text-xs text-amber-900">{dailyTip.categoryLabel}</span></span><span className="kid-tip-arrow text-lg font-bold" aria-hidden="true">⌄</span>
          </summary>
          <div className="mt-3 border-t border-amber-200 pt-3"><p className="text-xs font-bold uppercase tracking-wide text-amber-900">{getTipTypeLabel('kid', dailyTip.type)}</p><h3 className="mt-1 text-sm font-extrabold text-slate-900">{dailyTip.title}</h3><p className="mt-1 text-sm leading-relaxed text-slate-700">{dailyTip.text}</p></div>
        </details>}

        {!dashboardError && <Card className="p-3 sm:p-4">
          <div className="mb-2 flex items-center gap-2"><h2 className="flex-1 text-base font-extrabold text-slate-900">Recent Activity</h2><InfoTooltip compact label="About Recent Activity">Your latest money and savings activity. Available Money shows your balance immediately after each activity. Pending and rejected rewards do not increase your balance.</InfoTooltip></div>
          {recentActivity.length ? <div className="kid-recent-list -mx-3 sm:-mx-4 divide-y divide-slate-200">{recentActivity.map((activity) => {
            const savings = activity.activity_kind === 'savings';
            const transfer = activity.savings_activity_type === 'transfer';
            const returned = ['cancel', 'withdrawal'].includes(activity.savings_activity_type);
            const income = activity.type === 'income';
            const pending = activity.approval_status === 'pending';
            const rejected = activity.approval_status === 'rejected';
            const title = savings ? activity.display_title : activity.note || activity.category || activity.subtype || (income ? 'Money Added' : 'Money Spent');
            const icon = savings ? transfer ? 'move-between-goals' : returned ? 'withdraw-from-savings' : 'contribute-to-goal' : income ? 'money-received' : 'spent-money';
            const color = savings ? returned ? 'text-emerald-700' : transfer ? 'text-blue-700' : 'text-purple-700' : pending || rejected ? 'text-slate-500' : income ? 'text-emerald-700' : 'text-red-700';
            return <div key={activity.activity_id} className="kid-recent-row px-3 sm:px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 text-sm font-extrabold leading-snug text-slate-950"><MyPiggyIcon name={icon} size="xs" />{title}</p>
                  {savings && activity.display_subtitle && <p className="mt-1 text-sm font-bold text-slate-600">{activity.display_subtitle}</p>}
                  {pending && <p className="mt-1 text-xs font-bold text-amber-800">Waiting for approval</p>}
                  {rejected && <p className="mt-1 text-xs font-bold text-red-700">Rejected</p>}
                </div>
                <div className="shrink-0 text-right"><p className={`text-sm font-extrabold tabular-nums ${color}`}>{savings ? activity.display_sign : income ? '+' : '-'}{formatCurrency(activity.amount_cents)}</p>{savings && <p className="mt-0.5 text-xs font-bold text-slate-600">{activity.amount_label}</p>}</div>
              </div>
              <div className="mt-1.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-sm font-bold text-slate-600">
                <span className="whitespace-nowrap">{new Date(activity.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                <p className="ml-auto text-right">Available Money: <strong className="font-extrabold tabular-nums text-slate-950">{formatCurrency(activity.available_balance_after_cents)}</strong></p>
              </div>
            </div>;
          })}</div> : <p className="py-2 text-sm text-slate-600">Your money activity will appear here.</p>}
          <button type="button" onClick={() => navigate('/kid/reports')} className="mt-2 min-h-11 w-full rounded-lg bg-slate-100 px-3 py-2 text-sm font-extrabold text-slate-800 hover:bg-slate-200">View Activity in Reports →</button>
        </Card>}
        <Button variant="secondary" className="w-full" onClick={logoutKid}>Log Out</Button>
      </div>
      <KidOnboarding currentKid={currentKid} currency={kidFamily?.currency || 'USD'} refreshKid={refreshKid} onDashboardRefresh={async () => { await loadDashboardData(); refreshBalance(); }} />
      <KidDashboardTour open={showReplayTour} onExit={() => setShowReplayTour(false)} onFinish={() => setShowReplayTour(false)} onViewApps={() => { setShowReplayTour(false); navigate('/kid/dashboard', { state: { viewKidApps: true } }); }} finishLabel="All Done!" />
    </KidLayout>
  );
};

export default KidDashboard;
